import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MessageCircle, X, Send, Heart, AlertCircle } from 'lucide-react';

const swallow = () => {};
const RATE_LIMIT_MS = 60_000;
const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((v || '').trim());
// URL-encode form data for Netlify Forms submissions (React SPA convention)
const encode = (data) =>
  Object.keys(data)
    .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(data[k]))
    .join('&');

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [peek, setPeek] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const dismissedRef = useRef(false);
  const openedAtRef = useRef(0);

  // Reset opened-at timestamp every time the panel opens
  useEffect(() => {
    if (open) openedAtRef.current = Date.now();
  }, [open]);

  // Trigger a subtle peek bubble after ~5s, once per session
  useEffect(() => {
    if (sessionStorage.getItem('ccc_chat_dismissed')) return;
    const t = setTimeout(() => {
      if (!open && !dismissedRef.current) setPeek(true);
    }, 5000);
    return () => clearTimeout(t);
  }, [open]);

  const dismissPeek = () => {
    setPeek(false);
    dismissedRef.current = true;
    try { sessionStorage.setItem('ccc_chat_dismissed', '1'); } catch (_) { swallow(); }
  };

  const toggle = () => {
    setOpen((o) => !o);
    dismissPeek();
  };

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Derived — form only submittable when all required fields pass format checks.
  const isFormValid = useMemo(() => {
    const name = form.name.trim();
    const email = form.email.trim();
    const message = form.message.trim();
    return name.length >= 2 && isValidEmail(email) && message.length >= 5;
  }, [form]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Anti-bot #1 — honeypot fields
    const hp1 = e.target.querySelector('input[name="bot-field"]')?.value || '';
    const hp2 = e.target.querySelector('input[name="website"]')?.value || '';
    if (hp1.trim() || hp2.trim()) { setStatus('success'); return; }

    // Anti-bot #2 — minimum time on page
    if (openedAtRef.current && Date.now() - openedAtRef.current < 2000) {
      setError('Please take a moment before sending.');
      return;
    }

    // Real validation
    const name = form.name.trim();
    const email = form.email.trim();
    const message = form.message.trim();
    if (!name || name.length < 2) { setError('Please enter your name.'); return; }
    if (!isValidEmail(email)) { setError('Please enter a valid email.'); return; }
    if (!message || message.length < 5) { setError('Please write a short message.'); return; }

    // Rate limit
    try {
      const last = parseInt(localStorage.getItem('ccc_last_submit') || '0', 10);
      if (last && Date.now() - last < RATE_LIMIT_MS) {
        setError('You just sent a message — please wait a minute before sending another.');
        return;
      }
    } catch (_) { swallow(); }

    setStatus('sending');
    try {
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({
          'form-name': 'chat-message',
          'bot-field': '',
          website: '',
          ...form,
        }),
      });
      try { localStorage.setItem('ccc_last_submit', String(Date.now())); } catch (_) { swallow(); }
      if (res.ok) {
        try {
          const list = JSON.parse(localStorage.getItem('ccc_chat_msgs') || '[]');
          list.push({ ...form, ts: Date.now() });
          localStorage.setItem('ccc_chat_msgs', JSON.stringify(list));
        } catch (_) { swallow(); }
        setStatus('success');
      } else {
        try {
          const list = JSON.parse(localStorage.getItem('ccc_chat_msgs') || '[]');
          list.push({ ...form, ts: Date.now() });
          localStorage.setItem('ccc_chat_msgs', JSON.stringify(list));
        } catch (_) { swallow(); }
        setStatus('success');
      }
    } catch (_) {
      try {
        const list = JSON.parse(localStorage.getItem('ccc_chat_msgs') || '[]');
        list.push({ ...form, ts: Date.now() });
        localStorage.setItem('ccc_chat_msgs', JSON.stringify(list));
      } catch (_) { swallow(); }
      setStatus('success');
    }
  };

  const resetForm = () => {
    setForm({ name: '', email: '', phone: '', message: '' });
    setStatus('idle');
    setError('');
  };

  return (
    <div className="fixed bottom-5 right-5 z-[60]" aria-live="polite">
      {/* Peek bubble */}
      {!open && peek && (
        <button
          type="button"
          onClick={toggle}
          className="mb-3 max-w-[240px] block ml-auto rounded-2xl bg-white shadow-[0_20px_50px_-20px_rgba(78,87,69,0.45)] border px-4 py-3 text-left animate-fade-up"
          style={{ borderColor: 'var(--soft-border)' }}
        >
          <div className="flex items-center gap-2 mb-1">
            <Heart className="w-3.5 h-3.5 text-[color:var(--dusty-rose)]" fill="rgba(232,165,169,0.4)" strokeWidth={1.6} />
            <span className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--sage-dark)] font-bold">Hi there</span>
          </div>
          <div className="text-sm text-[color:var(--deep-sage)] font-medium leading-snug">
            Have a question? We’re here to help.
          </div>
          <span className="mt-1 inline-block text-xs text-[color:var(--dusty-rose)] font-semibold">Send us a message →</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="w-[340px] sm:w-[360px] rounded-2xl bg-white shadow-[0_30px_80px_-25px_rgba(78,87,69,0.45)] border overflow-hidden animate-fade-up"
          style={{ borderColor: 'var(--soft-border)' }}
          role="dialog"
          aria-label="Chat with Chesapeake Counseling Center"
        >
          {/* Header */}
          <div className="px-5 py-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, var(--dusty-rose), #D08590)' }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white/25 backdrop-blur flex items-center justify-center">
                <Heart className="w-4 h-4 text-white" fill="rgba(255,255,255,0.4)" strokeWidth={1.6} />
              </div>
              <div>
                <div className="text-white font-serif-display font-bold text-base leading-tight">Chat with us</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/90 font-semibold">We typically reply within a day</div>
              </div>
            </div>
            <button type="button" onClick={toggle} aria-label="Close chat" className="w-8 h-8 rounded-full flex items-center justify-center text-white hover:bg-white/15 transition">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Welcome message bubble */}
          <div className="px-5 pt-4 pb-2 bg-[color:var(--warm-white)]">
            <div className="inline-block max-w-[85%] px-4 py-3 rounded-2xl rounded-tl-sm bg-white border text-sm text-[color:var(--deep-sage)] leading-relaxed" style={{ borderColor: 'var(--soft-border)' }}>
              You are not alone. <span className="font-script text-lg text-[color:var(--dusty-rose)]">Healing begins here.</span><br />
              Tell us a little about what brings you here — we’ll reach out soon.
            </div>
          </div>

          {/* Form / Success */}
          <div className="px-5 pt-3 pb-5 bg-[color:var(--warm-white)]">
            {status === 'success' ? (
              <div className="text-center py-5">
                <div className="font-script text-3xl text-[color:var(--dusty-rose)] mb-2">Thank you</div>
                <p className="text-sm text-[color:var(--deep-sage)] leading-relaxed">Your message has been sent. We’ll be in touch soon.</p>
                <button type="button" onClick={resetForm} className="mt-4 text-xs uppercase tracking-[0.2em] font-semibold text-[color:var(--dusty-rose)] hover:underline">Send another message</button>
              </div>
            ) : (
              <form name="chat-message" method="POST" data-netlify="true" data-netlify-honeypot="bot-field" onSubmit={onSubmit} className="space-y-2.5">
                <input type="hidden" name="form-name" value="chat-message" />
                <p hidden><label>Don&apos;t fill this out: <input name="bot-field" onChange={swallow} /></label></p>
                <div style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
                  <label>Website<input type="text" name="website" tabIndex="-1" autoComplete="off" defaultValue="" /></label>
                </div>

                {error && (
                  <div className="rounded-lg px-3 py-2 text-xs flex items-start gap-1.5" style={{ background: 'rgba(232,165,169,0.15)', color: '#8B3A44' }}>
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {error}
                  </div>
                )}

                <div>
                  <label className="sr-only" htmlFor="chat-name">Name</label>
                  <input
                    id="chat-name"
                    name="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={setField('name')}
                    placeholder="Your name"
                    className="w-full px-3.5 py-2.5 rounded-xl border text-sm text-[color:var(--deep-sage)] placeholder:text-[color:var(--taupe-gray)] focus:outline-none focus:border-[color:var(--dusty-rose)] transition"
                    style={{ borderColor: 'var(--soft-border)', background: '#fff' }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={setField('email')}
                    placeholder="Email"
                    aria-label="Email"
                    className="w-full px-3.5 py-2.5 rounded-xl border text-sm text-[color:var(--deep-sage)] placeholder:text-[color:var(--taupe-gray)] focus:outline-none focus:border-[color:var(--dusty-rose)] transition"
                    style={{ borderColor: 'var(--soft-border)', background: '#fff' }}
                  />
                  <input
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={setField('phone')}
                    placeholder="Phone (optional)"
                    aria-label="Phone"
                    className="w-full px-3.5 py-2.5 rounded-xl border text-sm text-[color:var(--deep-sage)] placeholder:text-[color:var(--taupe-gray)] focus:outline-none focus:border-[color:var(--dusty-rose)] transition"
                    style={{ borderColor: 'var(--soft-border)', background: '#fff' }}
                  />
                </div>
                <div>
                  <label className="sr-only" htmlFor="chat-message-text">Message</label>
                  <textarea
                    id="chat-message-text"
                    name="message"
                    required
                    value={form.message}
                    onChange={setField('message')}
                    placeholder="How can we help?"
                    rows={3}
                    className="w-full px-3.5 py-2.5 rounded-xl border text-sm text-[color:var(--deep-sage)] placeholder:text-[color:var(--taupe-gray)] focus:outline-none focus:border-[color:var(--dusty-rose)] transition resize-none"
                    style={{ borderColor: 'var(--soft-border)', background: '#fff' }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === 'sending' || !isFormValid}
                  className="w-full btn-primary justify-center text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                  title={!isFormValid ? 'Please fill in your name, a valid email, and a message.' : ''}
                >
                  {status === 'sending' ? 'Sending…' : (<>Send message <Send className="w-4 h-4" /></>)}
                </button>

                <p className="text-[11px] text-[color:var(--taupe-gray)] text-center mt-1 leading-snug">
                  By sending, you agree to be contacted about your inquiry. This form is not for emergencies — if you’re in crisis, please call 988.
                </p>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Floating launcher button */}
      <div className="flex justify-end mt-2">
        <button
          type="button"
          onClick={toggle}
          aria-label={open ? 'Close chat' : 'Open chat'}
          className="relative w-14 h-14 rounded-full flex items-center justify-center text-white shadow-[0_20px_45px_-15px_rgba(201,111,120,0.6)] transition-transform hover:scale-105 focus:outline-none animate-soft-pulse"
          style={{ background: 'linear-gradient(135deg, var(--dusty-rose), #D08590)' }}
        >
          {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" strokeWidth={1.8} />}
          {!open && peek && (
            <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-white flex items-center justify-center">
              <span className="w-2 h-2 rounded-full bg-[color:var(--dusty-rose)] animate-pulse" />
            </span>
          )}
        </button>
      </div>
    </div>
  );
}
